// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { consoleService } from '../../services/consoleService';
import { Purchase, PurchaseSearchFilters } from '../../types/purchases';
import { authService } from '../../services/authService';
import { UserRole } from '../../types/auth';
import './Purchases.scss';

const Purchases: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState('');
  const [prevCursor, setPrevCursor] = useState('');

  const userRole = authService.getSessionRole();
  const canViewPurchases = userRole === UserRole.USER_ROLE_ADMIN;

  // Get filter values from URL params
  const userId = searchParams.get('user_id') || '';
  const productId = searchParams.get('product_id') || '';
  const transactionId = searchParams.get('transaction_id') || '';
  const store = searchParams.get('store') || '';
  const cursor = searchParams.get('cursor') || '';

  useEffect(() => {
    if (canViewPurchases) {
      loadPurchases();
    } else {
      setLoading(false);
    }
  }, [searchParams, canViewPurchases]);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      setError('');

      const filters: PurchaseSearchFilters = {
        user_id: userId || undefined,
        product_id: productId || undefined,
        transaction_id: transactionId || undefined,
        store: store || undefined,
      };

      const data = await consoleService.listPurchases(filters, cursor);
      setPurchases(data.purchases || []);
      setNextCursor(data.cursor || '');
      setPrevCursor(data.prev_cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<PurchaseSearchFilters>) => {
    const params = new URLSearchParams();
    
    if (newFilters.user_id) params.set('user_id', newFilters.user_id);
    if (newFilters.product_id) params.set('product_id', newFilters.product_id);
    if (newFilters.transaction_id) params.set('transaction_id', newFilters.transaction_id);
    if (newFilters.store) params.set('store', newFilters.store);
    
    setSearchParams(params);
  };

  const handlePageChange = (newCursor: string) => {
    const params = new URLSearchParams(searchParams);
    if (newCursor) {
      params.set('cursor', newCursor);
    } else {
      params.delete('cursor');
    }
    setSearchParams(params);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getStoreIcon = (store: string) => {
    switch (store.toLowerCase()) {
      case 'apple':
      case 'ios':
        return <i className="fab fa-apple text-dark" title="Apple App Store"></i>;
      case 'google':
      case 'android':
        return <i className="fab fa-google-play text-success" title="Google Play Store"></i>;
      case 'steam':
        return <i className="fab fa-steam text-primary" title="Steam"></i>;
      default:
        return <i className="fas fa-shopping-cart text-secondary" title={store}></i>;
    }
  };

  const getEnvironmentBadge = (environment: string) => {
    const isProduction = environment.toLowerCase() === 'production';
    return (
      <span className={`badge ${isProduction ? 'bg-success' : 'bg-warning'}`}>
        {environment}
      </span>
    );
  };

  if (!canViewPurchases) {
    return (
      <div className="purchases-container">
        <h2 className="pb-4">Purchases</h2>
        <div className="alert alert-warning">
          <strong>Access Denied:</strong> You don't have permission to view purchases.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="purchases-container">
        <h2 className="pb-4">Purchases</h2>
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading purchases...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="purchases-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Purchases</h2>
        <button
          className="btn btn-outline-primary"
          onClick={loadPurchases}
          title="Refresh purchases"
        >
          <i className="fas fa-sync-alt me-2"></i>
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">User ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by user ID..."
                value={userId}
                onChange={(e) => handleFilterChange({ user_id: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Product ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by product ID..."
                value={productId}
                onChange={(e) => handleFilterChange({ product_id: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Transaction ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by transaction ID..."
                value={transactionId}
                onChange={(e) => handleFilterChange({ transaction_id: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Store</label>
              <select
                className="form-select"
                value={store}
                onChange={(e) => handleFilterChange({ store: e.target.value })}
              >
                <option value="">All Stores</option>
                <option value="apple">Apple App Store</option>
                <option value="google">Google Play Store</option>
                <option value="steam">Steam</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Purchases Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Transaction ID</th>
                  <th>Product ID</th>
                  <th>User</th>
                  <th>Environment</th>
                  <th>Status</th>
                  <th>Purchase Time</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => (
                  <tr key={purchase.transaction_id}>
                    <td className="text-center">
                      {getStoreIcon(purchase.store)}
                    </td>
                    <td>
                      <code className="transaction-id" title={purchase.transaction_id}>
                        {truncateText(purchase.transaction_id, 20)}
                      </code>
                    </td>
                    <td>
                      <strong>{purchase.product_id}</strong>
                    </td>
                    <td>
                      <Link
                        to={`/accounts/${purchase.user_id}`}
                        className="text-decoration-none"
                      >
                        <code>{truncateText(purchase.user_id, 15)}</code>
                      </Link>
                    </td>
                    <td>
                      {getEnvironmentBadge(purchase.environment)}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        {purchase.refund_time ? (
                          <span className="badge bg-danger">Refunded</span>
                        ) : (
                          <span className="badge bg-success">Valid</span>
                        )}
                        {purchase.seen_before && (
                          <span className="badge bg-warning ms-1" title="Seen before">
                            Duplicate
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{formatDate(purchase.purchase_time)}</td>
                    <td>{formatDate(purchase.create_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {purchases.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No purchases found.</p>
            </div>
          )}

          {/* Pagination */}
          {(nextCursor || prevCursor) && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <small className="text-muted">
                  Showing {purchases.length} purchases
                </small>
              </div>
              <div className="btn-group">
                {prevCursor && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => handlePageChange('')}
                  >
                    First
                  </button>
                )}
                {prevCursor && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => handlePageChange(prevCursor)}
                  >
                    Previous
                  </button>
                )}
                {nextCursor && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => handlePageChange(nextCursor)}
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Purchase Statistics */}
      {purchases.length > 0 && (
        <div className="row mt-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-primary">{purchases.length}</h5>
                <p className="card-text">Total Purchases</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-success">
                  {purchases.filter(p => !p.refund_time).length}
                </h5>
                <p className="card-text">Valid Purchases</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-danger">
                  {purchases.filter(p => p.refund_time).length}
                </h5>
                <p className="card-text">Refunded</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title text-warning">
                  {purchases.filter(p => p.seen_before).length}
                </h5>
                <p className="card-text">Duplicates</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Purchases;
